import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { _chunk, _isNil, _isNotBlank, _isNotEmpty, _notNil, _set } from '@/littledash';

import type { AltId, Animal } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import type { CellChange, ChangeSource } from 'handsontable/common';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';

import { featuresSelector } from '@/support/Selectors';
import { ApiService } from '@/support/ApiService';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Spinner from '@/components/UI/Spinner';
import Switch from '@/components/UI/FormElements/Switch';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { modalAction, ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import { type AnimalIdentifierData, HoTcolumns } from './AssignIdentifiers.utils';
import AssignIdentifiersTableV16 from './AssignIdentifiersTableV16';
import BulkGenerateForm from './BulkGenerateForm';
import './AssignIdentifiers.scss';

const CHUNK_SIZE = 50;

const assignIdentifiersSelector = createSelector([featuresSelector], (features) => ({
  assignIdentifiersTagUnique: features?.assign_identifiers_tag_unique ?? false,
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

const altIdFields: Array<AltId> = ['tail', 'ear', 'tag', 'donor'];

/**
 * Determines whether a set of pending changes modifies an existing value (vs only adding new ones).
 * Adding an identifier to a previously-empty field is exempt from reason-for-change; changing or
 * clearing a field that already had a value requires a reason.
 */
const changesExistingValue = (
  pendingChanges: Record<ID, AnimalIdentifierData>,
  originalById: Map<ID, AnimalIdentifierData>
): boolean =>
  Object.values(pendingChanges).some((change) => {
    const original = originalById.get(change.id);
    if (_isNil(original)) {
      return false;
    }
    if (_isNotBlank(original.name) && original.name !== change.name) {
      return true;
    }
    return altIdFields.some((field) => {
      const before = original.alt_ids?.[field];
      const after = change.alt_ids?.[field];
      return _isNotBlank(before) && before !== after;
    });
  });

interface AssignIdentifiersProps {
  animals: Array<Animal>;
  handleCallback: (message?: string) => void;
  closeModal: () => void;
}

const initialAnimalData = (animals: Array<Animal>): Array<AnimalIdentifierData> =>
  animals.map(({ id, api_id, name, cage, alt_ids: altIds }) => ({
    id,
    api_id,
    name: name ?? '',
    cage,
    alt_ids: { ...altIds },
  }));

const AssignIdentifiers: FC<AssignIdentifiersProps> = ({ animals, handleCallback, closeModal }) => {
  const { assignIdentifiersTagUnique, reasonForChangeEnabled } = useSelector(assignIdentifiersSelector);
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const originalById = useMemo(
    () => new Map<ID, AnimalIdentifierData>(initialAnimalData(animals).map((a) => [a.id, a])),
    [animals]
  );
  const [data, setData] = useState<Array<AnimalIdentifierData>>(initialAnimalData(animals));
  const [changes, setChanges] = useState<Record<ID, AnimalIdentifierData>>({});
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [reloadOnClose, setReloadOnClose] = useState(false);

  const [updatingAnimals, setUpdatingAnimals] = useState(false);
  const [requestError, setRequestError] = useState<Error | false>(false);

  const updateAnimals = async (animals: Array<AnimalIdentifierData>, reasonForChange?: string) =>
    ApiService.call({
      endpoint: 'PATCH /api/v1/animals/identifiers',
      body: {
        animals: animals.map(({ api_id, name, alt_ids }) => ({
          api_id: api_id!,
          name,
          alt_ids: _isNotEmpty(alt_ids) ? alt_ids : null,
        })),
        ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
      },
      options: { onError: { throw: true, toast: false, slug: 'assign-identifiers-bulk-update' } },
    });

  const switchRef = useRef(false);
  const tableRef = useRef<HotTableRef>(null);

  const getHotInstance = () => tableRef.current?.hotInstance ?? null;

  const columns = useMemo(() => {
    if (assignIdentifiersTagUnique) {
      return HoTcolumns.filter((column) => column.data !== 'alt_ids.tag');
    }
    return HoTcolumns;
  }, [assignIdentifiersTagUnique]);

  const handleChangePayload = (pendingChanges: Record<ID, AnimalIdentifierData>) => Object.values(pendingChanges);

  const handleSave = async (reasonForChange?: string) => {
    getHotInstance()?.updateSettings({ readOnly: true });
    setUpdatingAnimals(true);
    setRequestError(false);
    try {
      await Promise.all(
        _chunk(handleChangePayload(changes), CHUNK_SIZE).map((batch) => updateAnimals(batch, reasonForChange))
      );
      if (autoSaveEnabled) {
        getHotInstance()?.updateSettings({ readOnly: false });
      } else {
        handleCallback('Successfully assigned identifiers.');
      }
    } catch (error) {
      setRequestError(error as Error);
      getHotInstance()?.updateSettings({ readOnly: false });
    } finally {
      setUpdatingAnimals(false);
    }
  };

  const handleSubmit = async () => {
    if (isInvalid) {
      return Promise.reject(new Error('Table data invalid'));
    }
    if (reasonForChangeEnabled && changesExistingValue(changes, originalById)) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => handleSave(reasonForChange),
      });
      return undefined;
    }
    return handleSave();
  };

  const handleBeforeChange = (dataChanges: Array<CellChange> | null) => {
    if (_isNil(dataChanges)) {
      return;
    }
    const { updatedData, updatedChanges } = dataChanges.reduce<{
      updatedData: Array<AnimalIdentifierData>;
      updatedChanges: Record<ID, AnimalIdentifierData>;
    }>(
      ({ updatedData, updatedChanges }, [row, column, , newValue]) => {
        _set(updatedData, `[${row}].${column}`, newValue);
        const { id } = animals[row];
        const { name, alt_ids: altIds } = updatedChanges?.[id] ?? animals[row];
        const updatedAnimal: AnimalIdentifierData = {
          id,
          api_id: animals[row].api_id,
          name: name ?? '',
          alt_ids: { ...altIds },
          cage: animals[row].cage,
        };
        _set(updatedAnimal, column as string, newValue);
        return {
          updatedData,
          updatedChanges: { ...updatedChanges, [id]: updatedAnimal },
        };
      },
      { updatedData: [...data], updatedChanges: { ...changes } }
    );
    setChanges(updatedChanges);
    setData(updatedData);
  };

  const handleAfterChange = (dataChanges: Array<CellChange> | null, source: ChangeSource | 'insert_row' | string) => {
    if (_isNil(dataChanges)) {
      return;
    }
    getHotInstance()?.validateRows(
      dataChanges.map((change) => change[0]),
      () => {
        if (source === 'edit' && !isInvalid && switchRef.current) {
          handleAutoSave(dataChanges);
        }
      }
    );
  };

  const handleAutoSave = (dataChanges: Array<CellChange>) => {
    const updates = dataChanges.map(([row, column, , newValue]) => {
      const { id } = animals[row];
      const { name, alt_ids: altIds } = changes[id] ?? animals[row];
      const updatedAnimal: AnimalIdentifierData = {
        id,
        api_id: animals[row].api_id,
        name: name ?? '',
        alt_ids: { ...altIds },
        cage: animals[row].cage,
      };
      _set(updatedAnimal, column as string, newValue);
      return updatedAnimal;
    });

    if (_isNotEmpty(updates)) {
      updateAnimals(updates).catch((error) => {
        setRequestError(error as Error);
      });
    }
  };

  const handleBulkAssign = (dataChanges: Array<CellChange>) => {
    setAutoSaveEnabled(false);
    switchRef.current = false;
    setReloadOnClose(false);
    const rowPropChanges = dataChanges.map(
      ([row, prop, value]) => [row, prop, value] as [number, string | number, unknown]
    );
    getHotInstance()?.setDataAtRowProp(rowPropChanges);
  };

  const handleExitModal = () => {
    if (!updatingAnimals) {
      if (autoSaveEnabled || reloadOnClose) {
        handleCallback();
      }
      closeModal();
    }
  };

  useEffect(() => {
    if (autoSaveEnabled && _isNotEmpty(Object.keys(changes))) {
      handleSubmit().catch(() => {
        setAutoSaveEnabled(false);
        switchRef.current = false;
        setReloadOnClose(true);
        toast.warning('Disabled autosave as pre existing changes have errors');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveEnabled]);

  const handleSwitch = () => {
    switchRef.current = !autoSaveEnabled;
    setAutoSaveEnabled(!autoSaveEnabled);
  };

  return (
    <ModalContainer size="wide" className="bg-white flex flex-column justify-between items-start">
      <ModalHeader
        title="Assign Identifiers to animals"
        closeModal={handleExitModal}
        closeDisabled={updatingAnimals}
        className="pa3 bb b--moon-gray w-100"
      />
      <div className="pa3 w-100">
        {requestError !== false && <ApiErrorBanner error={requestError as Error} className="mb3" />}
        <BulkGenerateForm data={animals} setData={handleBulkAssign} />
        <div className="ow-spreadsheet-styles assign-identifier-spreadsheet">
          <AssignIdentifiersTableV16
            data={data}
            columns={columns}
            innerRef={tableRef}
            beforeChange={handleBeforeChange}
            afterChange={handleAfterChange}
            maxRows={data.length}
            setInvalid={setIsInvalid}
          />
        </div>
      </div>
      <div className="pa3 bt b--moon-gray flex justify-between items-center w-100">
        <ModalActions
          onSubmit={handleSubmit}
          onCancel={handleExitModal}
          cancelBtnText="Close"
          submitButtonProps={{
            loading: updatingAnimals,
            disabled: updatingAnimals || autoSaveEnabled || isInvalid,
            tooltip: isInvalid ? spreadSheetValidationTooltip : undefined,
          }}
          cancelButtonProps={{ disabled: updatingAnimals }}
        />
        {!reasonForChangeEnabled && (
          <div className="flex items-center">
            {autoSaveEnabled && updatingAnimals && (
              <div className="flex items-center mr3">
                <Spinner size="small" className="mr2" color="near-black" />
                <span className="f6 lh-title near-black">Saving</span>
              </div>
            )}
            <Switch value={autoSaveEnabled} onChange={handleSwitch} disabled={false} />
            <label className="ml2 mb0">Auto save</label>
          </div>
        )}
      </div>
    </ModalContainer>
  );
};

export default AssignIdentifiers;
